import { Field, SelectInput } from "@components/ui/Field"
import { forwardRef, type SelectHTMLAttributes } from "react"
import { useTranslation } from "react-i18next"

import { ADMIN_ROLES, ROLE, type AdminRole } from "@/constants/roles"

interface Props extends SelectHTMLAttributes<HTMLSelectElement> {
  /** When false, the PLATFORM_SUPERUSER option is omitted — only a platform
   *  superuser may grant that role (PRD-32 §FR-3). */
  allowPlatformSuperuser: boolean
  /** The role currently chosen, so the hint can say what it allows. */
  selected: AdminRole
  error?: string
}

// Role <select> for the users create/edit dialogs. forwardRef so
// react-hook-form's register() works directly. The labels are the account
// menu's (`common:account.role.*`), so a role reads the same everywhere, and
// the hint says what the chosen role can do — the name alone never did.
export const RoleSelect = forwardRef<HTMLSelectElement, Props>(
  ({ allowPlatformSuperuser, selected, error, ...props }, ref) => {
    const { t } = useTranslation(["admin-users", "common"])
    const options = ADMIN_ROLES.filter(
      (role) => allowPlatformSuperuser || role !== ROLE.platformSuperuser,
    )

    return (
      <Field label={t("fields.role")} required hint={t(`role_hint.${selected}`)} error={error}>
        {({ id, describedBy, invalid }) => (
          <SelectInput
            ref={ref}
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            {...props}
          >
            {options.map((role) => (
              <option key={role} value={role}>
                {t(`common:account.role.${role}`)}
              </option>
            ))}
          </SelectInput>
        )}
      </Field>
    )
  },
)
RoleSelect.displayName = "RoleSelect"
